import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import axios from 'axios';
import { toast } from 'react-toastify';
import UserDetail from './UserDetails';
import UserRole from './UserRoles';
import UserPermission from './UserPermissions';
import authService from './api-authorization/AuthorizeService';

interface UserScreen {
    id: number;
    userDetails: UserDetails;
    userRoles: UserRoles[];
    userBranches: UserBranches[];
    userPermissions: PermissionGroup[];
    tab: string;
    loading: boolean;
}

interface UserDetails {
    id: number;
    firstName: string;
    lastName: string;
    companyId: number;
    departmentId: number;
    extension: string;
    mobile: string;
    email: string;
    emailSignatureId: number;
    userName: string;
    password: string;
    confirmPassword: string;
    driverSystem: boolean;
    skipTruckToolCheck: boolean;
    statusId: number;
}

interface UserRoles {
    id: number;
    name: string;
}

interface UserBranches {
    id: number;
    name: string;
}

interface PermissionGroup {
    groupId: number;
    group: string;
    permissions: UserPermissions[];
}

interface UserPermissions {
    id: number;
    name: string;
    groupId: number;
    selected: boolean;
}

interface INameToValueMap {
    [key: string]: any;
}

export class UserData extends React.Component<RouteComponentProps<{}>, UserScreen> {
    constructor(props: any) {
        super(props);

        this.state = {
            id: 0,
            userDetails: {
                id: 0,
                firstName: '',
                lastName: '',
                companyId: -1,
                departmentId: -1,
                extension: '',
                mobile: '',
                email: '',
                emailSignatureId: -1,
                userName: '',
                password: '',
                confirmPassword: '',
                driverSystem: false,
                skipTruckToolCheck: false,
                statusId: 1
            },
            userRoles: [],
            userBranches: [],
            userPermissions: [],
            tab: "Details",
            loading: true
        };     
    }

    componentDidMount() {
        this.getData();
    }

    async getData() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        let userParams: INameToValueMap = {};
        userParams["id"] = 0;
        userParams = this.props.match.params;
        let Id = userParams["id"];
        if (!Id) { Id = 0; }

        axios.get('api/User/CheckUserPermission?Permission=CreateEditUsers&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data == true) {
                //get user details
                if (Id > 0) {
                    //only get user details if we have searched for a user not creating a new one
                    axios.get('api/User/GetUserDetails?userId=' + Id + '&SubId=' + user.sub, {
                        headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                    })
                    .then(res => {
                        let userDet = res.data.userDetails;
                        userDet.password = "";  //used when adding new user
                        userDet.confirmPassword = "";

                        this.setState({ id: Id, userDetails: userDet, userRoles: res.data.userRoles, userBranches: res.data.userBranches, userPermissions: res.data.userPermissions, loading: false });
                    })
                    .catch(error => {
                        toast.error(error.message);
                    });
                } else {
                    axios.get('api/User/GetPermissions', {
                        headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                    })
                    .then(res => {
                        this.setState({ id: Id, userPermissions: res.data.permissionGroups, loading: false });
                    })
                    .catch(error => {
                        toast.error(error.message);
                    });
                }
            } else {
                //redirect to 403 permission denied
                this.props.history.push("/accessdenied")
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    tabSelected(tab: string) {
        this.setState({ tab: tab });
    }

    tabChanged = (e: any) => {
        this.tabSelected(e.target.value);
    }

    noChange = (e: any) => {
        //no changes as user doesn't actually click on the radio button
    }

    handleChange = (e: any) => {
        let change: any = {};
        change[e.target.name] = e.target.value;
        this.setState(change);
    }

    updateDetails = (userDetails: UserDetails) => {
        this.setState({ userDetails: userDetails });
    }

    updateRoles = (userRoles: UserRoles[]) => {
        this.setState({ userRoles: userRoles });
    }

    updateBranches = (userBranches: UserBranches[]) => {
        this.setState({ userBranches: userBranches });
    }

    updateDriverSystem = (driverSytem: boolean, skipTruckToolCheck: boolean) => {
        var userDetails = this.state.userDetails;
        userDetails.driverSystem = driverSytem;
        userDetails.skipTruckToolCheck = skipTruckToolCheck;
        this.setState({ userDetails: userDetails });
    }

    updatePermissions = (userPermissions: PermissionGroup[]) => {
        this.setState({ userPermissions: userPermissions });
    }

    validateAll = () => {
        let formIsValid = true;
        var details = this.state.userDetails;
        if (!details.firstName || !details.lastName || details.departmentId < 1 || !details.email || !details.userName || details.statusId < 0) {
            toast.error("User Details must be completed!");
            formIsValid = false;
        }
        if (this.state.id == 0) {
            //check password - if existing user, separate form to change password
            if (!details.password || !details.confirmPassword) {
                toast.error("User Details - Password and Confirm Password are required");
                formIsValid = false;
            }
            if (details.password && details.confirmPassword) {
                //check password and confirm password match
                if (details.password != details.confirmPassword) {
                    toast.error("User Details - Password and Confirm Password must match");
                    formIsValid = false;
                }
            }
        }

        if (this.state.userRoles.length < 1) {
            toast.error("Roles/Branches - At least one role is required");
            formIsValid = false;
        }

        if (this.state.userBranches.length < 1) {
            toast.error("Roles/Branches - At least one branch is required");
            formIsValid = false;
        }
        return formIsValid;
    }

    save = () => {
        //validate all data before saving
        if (this.validateAll()) {
            this.saveUser();
        }
    }

    async saveUser() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var regUser = {
            userDetails: this.state.userDetails,
            userRoles: this.state.userRoles,
            userBranches: this.state.userBranches,
            userPermissions: this.state.userPermissions,
            subId: user.sub
        };
        
        if (this.state.id == 0) {
            //create new user
            axios.post('Account/Register', regUser, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(res => {
                if (res.data.isError) {
                    var errors = res.data.messages as any[];
                    errors.map(function (error: any) {
                        toast.error(error.content);
                    });
                } else {
                    toast.success("User Saved");
                    //then need to go to job search?
                    this.props.history.push("/usersearch");
                }

            })
            .catch(error => {
                toast.error(error.message);
            });
        } else {
            axios.post('api/User/Save', regUser, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(res => {
                toast.success("User Saved");
                //then need to go to job search?
                this.props.history.push("/usersearch");
            })
            .catch(error => {
                toast.error(error.message);
            });
        }
        
    }

    public render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderUser();
   
        return <div>
            <h1>User</h1>
            {contents}
        </div>;
    }

    renderUser = () => {
        var contents;
        if (this.state.tab == "Details") {
            contents = <UserDetail id={this.state.id} userDetails={this.state.userDetails} update={this.updateDetails} save={this.save} />
        }
        else if (this.state.tab == "Roles") {
            contents = <UserRole id={this.state.id} driverSystem={this.state.userDetails.driverSystem} skipTruckToolCheck={this.state.userDetails.skipTruckToolCheck} userRoles={this.state.userRoles} userBranches={this.state.userBranches} updateRoles={this.updateRoles} updateBranches={this.updateBranches} updateDriverSystem={this.updateDriverSystem} save={this.save} />
        }
        else if (this.state.tab == "Permissions") {
            contents = <UserPermission id={this.state.id} userPermissions={this.state.userPermissions} update={this.updatePermissions} save={this.save} />
        }

        return (
            <div>
                <div className="tabsComponent" id="tab-container">

                    <div className="tabsComponent__showDropdown">
                        <label className="input-group" htmlFor="userTab">
                            <span className="label">Item Type</span>
                            <select className="select" id="userTab" value={this.state.tab} onChange={this.tabChanged} >
                                <option value="Details">Details</option>
                                <option value="Roles">Roles/Branches</option>
                                <option value="Permissions">Permissions</option>
                            </select>
                        </label>
                    </div>

                    <div className="tabsComponent__tabs tabsComponent__hideTabs">
                        <input type="radio" name="tabs" id="tab1" className="tabsComponent__control" checked={this.state.tab == 'Details'} onChange={(e) => this.noChange(e)}></input>
                        <label className="tabsComponent__label" htmlFor="tabItem" onClick={() => this.tabSelected('Details')}>Details</label>
                        <input type="radio" name="tabs" id="tab2" className="tabsComponent__control" checked={this.state.tab == 'Roles'} onChange={(e) => this.noChange(e)}></input>
                        <label className="tabsComponent__label" htmlFor="tabItem" onClick={() => this.tabSelected('Roles')}>Roles/Branches</label>
                        <input type="radio" name="tabs" id="tab3" className="tabsComponent__control" checked={this.state.tab == 'Permissions'} onChange={(e) => this.noChange(e)}></input>
                        <label className="tabsComponent__label" htmlFor="tabItem" onClick={() => this.tabSelected('Permissions')}>Permissions</label>
                    </div>

                    <div className="tabsComponent__content tabsComponent__content--selected">
                        <div>
                            {contents}
                        </div>
                    </div>

                </div>

            </div>


        );
    }

}
